import { getTypographyMetric, getTypographyMetricOverride } from '../../../../../../../../helpers';
import { useTheme } from 'styled-components';
import { Button } from 'front-commons/ds/components/Button';
import { Flex } from 'front-commons/ds/components/Flex';
import { Icon } from 'front-commons/ds/components/Icon';
import { SideDrawer } from 'front-commons/ds/components/SideDrawer';
import { Skeleton } from 'front-commons/ds/components/Skeleton';
import { Typography } from 'hype-ds';
import { animated } from 'react-spring';
import { useHeader } from '../../../../../../../../context';
import { useCategoryRouteParams } from '../../hooks/useCategoryRouteParams';
import { MenuButton } from './components/MenuButton';
import { useMenuAnimation } from './hooks/useMenuAnimation';
import { useMenuSelection } from './hooks/useMenuSelection';
import { CategoriesMobileProps } from './interfaces';

const AnimatedContainer = animated(Flex);

export function CategoriesMobile({ open, onClose }: CategoriesMobileProps) {
	const { fontSizes } = useTheme();

	const {
		categories: { useGetCategoriesPh, posId },
	} = useHeader();

	const { data, isFetching } = useGetCategoriesPh({ posId, enabled: open });

	const { departmentId, categoryId, subcategoryId, goToCategory } = useCategoryRouteParams();

	const {
		selectedDepartment,
		selectedCategory,
		selectedItemName,
		shouldShowBackButton,
		currentView,
		direction,
		handleBack,
		handleSelectDepartment,
		handleSelectCategory,
		handleSelectSubcategory,
	} = useMenuSelection({
		onSubcategorySelect: goToCategory,
		onClose,
	});

	const { transitions } = useMenuAnimation({ currentView, direction });

	if (!open) return <></>;

	return (
		<SideDrawer open side="top" padding="1rem 1.5rem">
			<Flex direction="column">
				<Flex alignItems="center" justifyContent="space-between" margin="0 0 1rem">
					<Flex
						variant="none"
						opacity={shouldShowBackButton ? '1' : '0'}
						pointerEvents={shouldShowBackButton ? 'auto' : 'none'}
						as={Button}
						gap="8px"
						position="relative"
						alignItems="center"
						justifyContent="center"
						noShrink
						onClick={handleBack}
					>
						<Icon name="chevron_left" color="--text-primary" size="24px" />
					</Flex>
					<Button variant="none" onClick={onClose}>
						<Icon name="close" color="--text-primary" size="24px" />
					</Button>
				</Flex>

				{isFetching && !data?.length && (
					<Flex direction="column" gap="0.5rem" width="100%">
						{Array.from({ length: 6 }).map((_, i) => (
							<Skeleton key={`mobile-category-skeleton-${i}`} height="40px" />
						))}
					</Flex>
				)}

				{!!data?.length && (
					<Flex
						raw={{
							"[data-header-text='CategoriesMobile-text-1']": {
								display: 'inline',
								fontSize: getTypographyMetricOverride(fontSizes['12']),
								lineHeight: getTypographyMetricOverride(fontSizes['20']),
							},
						}}
						direction="column"
						gap="0.5rem"
						width="100%"
					>
						<Typography
							textAlign="inherit"
							fontSize={getTypographyMetric(fontSizes['12'])}
							fontWeight="400"
							lineHeight={getTypographyMetric(fontSizes['20'])}
							color="--greys-700"
							data-header-text="CategoriesMobile-text-1"
						>
							{selectedItemName}
						</Typography>

						<Flex
							position="relative"
							width="100%"
							overflow="hidden auto"
							minHeight="50vh"
							maxHeight="calc(100vh - 100px)"
						>
							{transitions((style, item) => {
								return (
									<AnimatedContainer
										display="block"
										gap="normal"
										position={currentView === item ? 'relative' : 'absolute'}
										width="100%"
										top="0"
										left="0"
										key={item}
										style={style}
									>
										{item === 'departments' && (
											<Flex direction="column" gap="0.5rem" width="100%">
												{data?.map((department) => (
													<MenuButton
														name={department.name}
														selected={department.id === departmentId}
														onClick={() => {
															handleSelectDepartment(department);
														}}
													/>
												))}
											</Flex>
										)}

										{item === 'categories' && selectedDepartment && (
											<Flex direction="column" gap="0.5rem" width="100%">
												{selectedDepartment?.children?.map((category) => (
													<MenuButton
														name={category.name}
														selected={category.id === categoryId}
														onClick={() => {
															handleSelectCategory(category);
														}}
													/>
												))}
											</Flex>
										)}

										{item === 'subcategories' && selectedCategory && (
											<Flex direction="column" gap="0.5rem" width="100%">
												{selectedCategory?.children?.map((subcategory) => (
													<MenuButton
														name={subcategory.name}
														selected={subcategory.id === subcategoryId}
														onClick={() => {
															handleSelectSubcategory(subcategory.id);
														}}
													/>
												))}
											</Flex>
										)}
									</AnimatedContainer>
								);
							})}
						</Flex>
					</Flex>
				)}
			</Flex>
		</SideDrawer>
	);
}
